import {
  createFileRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import React, { useEffect } from "react";
import {
  AppShell,
  AppShellProvider,
  DialogHostProvider,
  useAppShell,
  useShellTier,
} from "@salestrak/ui";

import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useSidebarToggleHotkey } from "#app/lib/hooks/use-sidebar-collapsed";
import { useIsSettingsRoute } from "#app/lib/hooks/use-is-settings-route";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { markWorkspaceOpened } from "#app/lib/workspace-recents";
import { INBOX_SUBSCRIPTIONS } from "#app/hooks/inbox/inbox-subscription";
import { useRegisterSubscriptions } from "#app/lib/subscriptions/subscription-provider";
import { useLiveWorkspaceSync } from "#app/lib/subscriptions/live-workspace-sync";
import { useWorkspaceRouteValidation } from "#app/lib/hooks/use-workspace-route-validation";
import { useTrackScreenView } from "#app/lib/analytics";
import { WorkspaceNotFoundScreen } from "#app/components/app-error-screens";
import { WorkspacePendingDeletionScreen } from "#app/components/workspace-deletion/pending-deletion-screen";
import { SettingsSidebar } from "#app/components/navigation/settings-sidebar";
import { useCreateProjectHotkey } from "#app/lib/hooks/use-create-project-hotkey";
import { useModuleHotkeys } from "#app/lib/hooks/use-module-hotkeys";
import { useCompactModeHotkey } from "#app/lib/hooks/use-compact-mode-hotkey";
import { useKeyboardShortcutsHotkey } from "#app/lib/hooks/use-keyboard-shortcuts-hotkey";
import { useLogoutHotkey } from "#app/lib/hooks/use-logout-hotkey";
import { useCtrlKShortcut } from "#app/components/global-search/use-ctrl-k-shortcut";
import {
  MemberProfilePanelHost,
  useProfileIslandOpen,
} from "#app/components/entity";
import {
  HostedDialogMounts,
  WorkspaceDialogMounts,
} from "#app/components/workspace-dialog-mounts";
import { pageTitleFor } from "#app/components/navigation/page-title";
import { SiteHeader } from "#app/components/navigation/site-header";
import {
  MODULE_RAIL_WIDTH,
  MODULE_RAIL_WIDTH_COMPACT,
  ModuleRail,
  ModuleStrip,
  SETTINGS_BROWSE_ID,
  useModuleRail,
} from "#app/components/navigation/module-rail";
import { ModuleMenuForTile } from "#app/components/navigation/module-menu-for-tile";
import { WidgetProviders } from "#app/widgets/widget-providers";

// The persisted `sidebar-prefs.width` is the module-menu PANE width — the
// module rail is a separate column inside the same Sidebar, so compact mode
// narrows the rail without resizing the pane. The desktop drag seam and the
// rendered width both clamp to [MIN, MAX], so any persisted width outside
// those bounds is brought back into range on read.
const MIN_SIDEBAR_PANE_WIDTH = 210;
const MAX_SIDEBAR_PANE_WIDTH = 480;
const DEFAULT_SIDEBAR_PANE_WIDTH = 254;

// The right-side island (AppShell rightPanel): the profile island. The shell
// owns the drag seam (the grab handle in the expanded island's leading
// gutter) — the layout only persists its width, clamped back into range on
// read, and publishes it to the shell.
const MIN_RIGHT_PANEL_WIDTH = 360;
const MAX_RIGHT_PANEL_WIDTH = 720;
const DEFAULT_PROFILE_PANEL_WIDTH = 370;

const clampRightPanelWidth = (width: number) =>
  Math.min(MAX_RIGHT_PANEL_WIDTH, Math.max(MIN_RIGHT_PANEL_WIDTH, width));

/**
 * The layout holds NO route subscription of its own. Everything that reads the
 * location lives in one of the null-rendering leaves below (or in the sidebar,
 * which subscribes for itself), so a navigation re-renders the Outlet and
 * those leaves — never the shell around them. With the subscription up in
 * `WorkspaceLayout`, every route change re-rendered the rail, the island and
 * every mounted dialog in one synchronous pass, which is a visible stall on
 * every click in the settings sidebar. `useIsSettingsRoute` is the one
 * boolean subscription the layout itself holds.
 */

/** Effects keyed on the route: the $workspace param check and screen-view
 *  analytics. Both subscribe to router state, so they live in a leaf. */
function RouteEffects() {
  useWorkspaceRouteValidation();
  useTrackScreenView();
  return null;
}

const renderHostedDialogMounts = () => <HostedDialogMounts />;

function DocumentTitle() {
  const { activeWorkspace } = useWorkspace();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isSettingsRoute = useIsSettingsRoute();
  const routePath = activeWorkspace?.urlPath
    ? pathname.replace(`/${activeWorkspace.urlPath}`, "") || "/"
    : pathname;

  React.useEffect(() => {
    const title = pageTitleFor(routePath);
    if (isSettingsRoute) {
      document.title = title
        ? `${title} - Settings - SalesTrak`
        : "Settings - SalesTrak";
    } else {
      document.title = title ? `${title} - SalesTrak` : "SalesTrak";
    }
    // `routePath` is derived from the pathname and the workspace slug, so it
    // covers both without listing them.
  }, [routePath, isSettingsRoute]);

  return null;
}

function ShortcutListener() {
  // Mobile: ⌘K opens the global-search drawer (inert on desktop).
  // Desktop ⌘K opens the top-centered command dialog from GlobalSearchBar.
  useCtrlKShortcut();
  // ⌥/Alt+P opens the create-project dialog — the hotkey the module-menu
  // create popovers advertise on their Project row.
  useCreateProjectHotkey();
  // `G` then a letter navigates to a module — the chord the rail tooltips
  // advertise.
  useModuleHotkeys();
  // ⌥/Alt+M toggles the rail's compact mode — the chord the Customize
  // Navigation dialog's Compact mode switch advertises.
  useCompactModeHotkey();
  // ⌘/Ctrl+/ opens the keyboard-shortcuts reference — the chord the dialog
  // itself and the account menu advertise.
  useKeyboardShortcutsHotkey();
  // ⌥/Alt+⇧+Q opens the log-out confirmation — the chord the account menu's
  // Log out row advertises.
  useLogoutHotkey();
  return null;
}

/**
 * Desktop ⌘/Ctrl+\ toggles the sidebar pane. Separate from ShortcutListener
 * because it reads the shell's collapsed state, which only AppShellProvider
 * descendants can reach.
 */
function SidebarShortcutListener() {
  useSidebarToggleHotkey();
  return null;
}

/**
 * Auto-closes the compact drawer on route change — a picked row is a commit,
 * and the page it landed on is the point. The medium pane deliberately does
 * NOT close here: it is a pinned column the user expanded, and it stays until
 * they collapse it.
 *
 * It lives inside `AppShellProvider` because the drawer is the shell's own
 * ephemeral state, which only descendants can reach.
 */
function NavOverlayAutoClose() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { tier, openMobile, setOpenMobile } = useAppShell();
  const { setBrowseTileId } = useModuleRail();
  const prevPathname = React.useRef(pathname);

  React.useEffect(() => {
    if (pathname === prevPathname.current) return;
    prevPathname.current = pathname;
    if (tier !== "compact") return;
    // A landed navigation ends any drawer browsing — the menu follows the
    // route again.
    setBrowseTileId(null);
    setOpenMobile(false);
  }, [pathname, tier, setBrowseTileId, setOpenMobile]);

  // Dismissing the drawer abandons any browsing: on the next open the menu
  // matches where the user actually is, not where they last window-shopped.
  React.useEffect(() => {
    if (!openMobile) setBrowseTileId(null);
  }, [openMobile, setBrowseTileId]);

  return null;
}

/**
 * Settings has no rail tile, so with the pane away there is nothing on screen
 * naming where you are or letting you move to another settings page — the
 * pane IS settings' navigation. Entering settings therefore presents it
 * (including a direct load of a settings URL), once per entry: on the full
 * tier by expanding the pinned pane (persisted like any other expand, so
 * leaving settings does not snap it shut again), on medium by summoning the
 * flyout. Compact keeps the drawer closed — the settings mobile header's
 * trigger is the entry there.
 */
function SettingsNavAutoOpen() {
  const isSettingsRoute = useIsSettingsRoute();
  const { tier, open, setOpen, setOpenOverlay } = useAppShell();
  const wasSettingsRoute = React.useRef(false);

  React.useEffect(() => {
    const entered = isSettingsRoute && !wasSettingsRoute.current;
    wasSettingsRoute.current = isSettingsRoute;
    if (!entered) return;
    if (tier === "medium") {
      setOpenOverlay(true);
    } else if (tier === "full" && !open) {
      setOpen(true);
    }
  }, [isSettingsRoute, tier, open, setOpen, setOpenOverlay]);

  return null;
}

function WorkspaceLayout() {
  const tier = useShellTier();
  const isSettingsRoute = useIsSettingsRoute();

  const { activeTileId, browseTileId, compact } = useModuleRail();
  const { state: sidebarPrefs, update: updateSidebarPrefs } = usePersistedState(
    "sidebar-prefs",
    { width: DEFAULT_SIDEBAR_PANE_WIDTH, expanded: true },
  );
  const handleSidebarWidthChange = React.useCallback(
    (width: number) => updateSidebarPrefs({ width }),
    [updateSidebarPrefs],
  );

  const { state: profilePanelPrefs, update: updateProfilePanelPrefs } =
    usePersistedState("profile-panel-prefs", {
      width: DEFAULT_PROFILE_PANEL_WIDTH,
    });
  const profilePanelWidth = clampRightPanelWidth(profilePanelPrefs.width);

  // Desktop expanded-vs-collapsed only. The mobile drawer is the shell's own
  // `openMobile` (ephemeral, never persisted), so it is a separate state from
  // this one. Seed from the synchronously-read preference so the first paint
  // already matches it.
  const [open, setOpen] = React.useState(() => sidebarPrefs.expanded);
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      updateSidebarPrefs({ expanded: next });
    },
    [updateSidebarPrefs],
  );

  useRegisterSubscriptions(INBOX_SUBSCRIPTIONS);
  // Settings, topology, sharing, roles and flags stay live without a reload.
  useLiveWorkspaceSync();

  // The profile island presents whenever a user entity is active
  // (EntityDetailProvider lives above this layout in AppProviders).
  const isProfilePanelOpen = useProfileIslandOpen();

  const rightPanelWidth = profilePanelWidth;
  const handleRightPanelWidthChange = React.useCallback(
    (width: number) => {
      updateProfilePanelPrefs({ width });
    },
    [updateProfilePanelPrefs],
  );

  // The tile being SHOWN drives which module menu fills the sidebar pane —
  // normally the route's active tile, but the compact drawer can browse
  // another module's menu without navigating (`browseTileId`). Settings
  // routes keep their own dedicated sidebar, and the drawer can browse INTO
  // it from an app route or OUT of it to a module's menu on a settings route
  // (`useSettingsDoors`) — the browse wins over the route either way.
  const showSettingsNav =
    browseTileId === SETTINGS_BROWSE_ID ||
    (isSettingsRoute && browseTileId === null);
  const menuTileId = browseTileId ?? activeTileId;
  const sidebarContent = showSettingsNav ? (
    <SettingsSidebar />
  ) : (
    <ModuleMenuForTile tileId={menuTileId} />
  );

  // Compact mode renders icon-only tiles, so the rail column narrows. The
  // preference alone decides it at every tier — the medium tier's overlay
  // pane rides whichever rail edge results.
  const railWidth = compact ? MODULE_RAIL_WIDTH_COMPACT : MODULE_RAIL_WIDTH;
  const paneWidth = Math.min(
    MAX_SIDEBAR_PANE_WIDTH,
    Math.max(MIN_SIDEBAR_PANE_WIDTH, sidebarPrefs.width),
  );

  // Shell v2 (desktop only): the rail replaces the header tab strip. The
  // workspace tile heads the rail, module menus name themselves in the pane
  // header, and search + account live in the content column's top row. Mobile
  // keeps the drawer shell in both worlds.

  return (
    <WidgetProviders>
      <RouteEffects />
      <DocumentTitle />
      <ShortcutListener />
      {/* App-level dialogs render inside the innermost open sheet, so one
          opened from a sheet stacks on it (HostedDialogMounts). */}
      <DialogHostProvider render={renderHostedDialogMounts}>
        {/*
          * The shell: on desktop, one row of module rail + module-menu pane +
          * inset content, with no header above it — the rail carries the
          * workspace mark, the account, and the modules, and search lives on the
          * pane header. Mobile puts all three columns plus SiteHeader inside the
          * nav drawer, so the header is passed only there. Collapsing (`open`
          * false) clips the pane away and leaves the rail. The rail/pane split
          * is app domain; AppShell owns the surrounding layout and the mobile
          * drawer. The pane resize seam belongs to the pane component itself
          * (ModuleMenu / SettingsSidebar) — it reads and writes the width
          * published here through AppShellProvider.
          */}
        <AppShellProvider
          maxRightPanelWidth={MAX_RIGHT_PANEL_WIDTH}
          maxSidebarWidth={MAX_SIDEBAR_PANE_WIDTH}
          minRightPanelWidth={MIN_RIGHT_PANEL_WIDTH}
          minSidebarWidth={MIN_SIDEBAR_PANE_WIDTH}
          onOpenChange={handleOpenChange}
          onRightPanelWidthChange={handleRightPanelWidthChange}
          onSidebarWidthChange={handleSidebarWidthChange}
          open={open}
          rightPanelWidth={rightPanelWidth}
          sidebarWidth={paneWidth}
        >
          <NavOverlayAutoClose />
          <SettingsNavAutoOpen />
          <SidebarShortcutListener />
          <AppShell
            moduleRail={<ModuleRail />}
            moduleRailWidth={railWidth}
            // Slack's arrangement: the rail wears no card and sits on the shell
            // fill, and the module menu joins the page in ONE card split by a
            // seam. A page-owned canvas (the org chart's dot matrix, the board,
            // the map) is then clipped by the card that owns it — nothing
            // floats over a background it is not actually above.
            navChrome="with-page"
            // The right-side island: the member profile.
            rightPanel={{
              open: isProfilePanelOpen,
              content: <MemberProfilePanelHost />,
              resizeLabel: "Resize profile panel",
            }}
            // The strip is the compact drawer's tier 1. The settings pane keeps
            // the drawer to itself — its "Back to app" is the way out, and a
            // module strip above it would blur which world you are in.
            moduleStrip={
              tier === "compact" && !showSettingsNav ? <ModuleStrip /> : undefined
            }
            sidebar={sidebarContent}
            siteHeader={tier === "compact" ? <SiteHeader /> : undefined}
          >
            <Outlet />
          </AppShell>
        </AppShellProvider>
        <WorkspaceDialogMounts />
      </DialogHostProvider>
    </WidgetProviders>
  );
}

/**
 * Gate above the shell: the $workspace param must name a workspace this
 * account can see. The parent (app) layout holds this subtree behind the
 * brand loading screen while account data loads, so a miss here is a real
 * verdict, not a race — except mid-switch, where `useWorkspaceRouteValidation`
 * (inside the shell) is about to rewrite the param; the list check covers
 * that too, since a switch target is by definition in the list.
 */
function WorkspaceRoute() {
  const { workspace } = Route.useParams();
  const { workspaces } = useWorkspace();

  const match = workspaces.find((w) => w.urlPath === workspace);
  // The picker orders by when this device last opened each workspace; a
  // live workspace rendering here IS that moment. Frozen ones do not count —
  // the pending screen is not "opening" the workspace.
  const openedId = match && !match.deletion ? match.workspaceId : null;
  useEffect(() => {
    if (openedId) markWorkspaceOpened(openedId);
  }, [openedId]);
  if (!match) {
    return <WorkspaceNotFoundScreen urlPath={workspace} />;
  }
  // Frozen: the API refuses the shell's every query, so the shell never
  // mounts — the pending screen stands in its place for every member.
  if (match.deletion) {
    return <WorkspacePendingDeletionScreen workspace={match} />;
  }
  return <WorkspaceLayout />;
}

export const Route = createFileRoute("/(app)/$workspace")({
  component: WorkspaceRoute,
});
